export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };
export function isConfigObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function humanizeConfigKey(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').replace(/^./, value => value.toUpperCase());
}
export function getConfigPath(value: unknown, path: readonly string[]): unknown {
  return path.reduce<unknown>((current, key) => isConfigObject(current) || Array.isArray(current) ? (current as Record<string, unknown>)[key] : undefined, value);
}
/** Immutable path replacement retains all sibling, root, and unknown configuration. */
export function setConfigPath(value: unknown, path: readonly string[], replacement: unknown): unknown {
  if (!path.length) return structuredClone(replacement);
  const [key, ...rest] = path;
  if (['__proto__', 'constructor', 'prototype'].includes(key)) throw new Error('Invalid configuration path');
  if (Array.isArray(value)) {
    const next = [...value];
    next[Number(key)] = setConfigPath(next[Number(key)], rest, replacement);
    return next;
  }
  const current = isConfigObject(value) ? value : {};
  return { ...current, [key]: setConfigPath(current[key], rest, replacement) };
}
export function isJsonValue(value: unknown, depth = 0): value is JsonValue {
  if (depth > 40) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.length <= 10000 && value.every(item => isJsonValue(item, depth + 1));
  return isConfigObject(value) && Object.entries(value).every(([key, item]) => !['__proto__', 'constructor', 'prototype'].includes(key) && isJsonValue(item, depth + 1));
}
